import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { RoundedXMarkIcon } from '~/components/atoms/icon/xMark/RoundedXMarkIcon';
import InstantSearchSidebar from '~/components/organisms/instantSearch/InstantSearchSidebar';
import InstantProductResults from '~/components/organisms/instantSearch/products/InstantProductResults';
import { useRenderingInfo } from '~/contexts/useRenderingInfo';
import { useConfig } from '~/core/configuration/configContext';
import { useSERPActions } from '~/core/state/actions/SERPActionsContext';
import { useSearchQuery } from '~/core/state/searchQueryContext';
import { useDynamicComponents } from '~/plugins/dynamicComponents/dynamicComponentsContext';
import isInputElement from '~/utils/isInputElement';
import styles from './instantSearch.module.scss';

export interface IInstantSearchProps {
    openFullSERP: () => void;
    closeSERP: () => void;
    innerSERPRef: React.RefObject<HTMLDivElement>;
}

const InstantSearch: React.FC<IInstantSearchProps> = ({ openFullSERP, closeSERP, innerSERPRef }) => {
    const { InstantSearchBackdrop } = useDynamicComponents();
    const { searchQuery, updateSearchQuery } = useSearchQuery();
    const { handleGoToResult } = useSERPActions();

    const instantSearchRef = React.useRef<HTMLDivElement>(null);
    const caretRef = React.useRef<HTMLDivElement>(null);
    const config = useConfig();
    const [instantSearchOpen, setInstantSearchOpen] = useState(false);
    const instantSearchConfig = config.layout.configuration.instantSearch;
    const { isMobileVersion: isMobile } = useRenderingInfo();

    const positionInstantSearch = (nativeSearchElement: Element) => {
        const inputRect = nativeSearchElement.getBoundingClientRect();

        if (inputRect.width === 0 || inputRect.bottom === 0 || instantSearchRef.current === null) {
            return;
        }

        instantSearchRef.current.style.top = `${inputRect.bottom + window.scrollY}px`;
        //instantSearchRef.current.style.left = `${inputRect.left}px`;
        //instantSearchRef.current.style.width = `${inputRect.width}px`;

        // caret positioning
        if (caretRef.current) {
            const instantSearchRect = instantSearchRef.current.getBoundingClientRect();
            caretRef.current.style.left = `${inputRect.left - instantSearchRect.left + 40}px`;
        }
    };

    /*
     * Handles the following:
     * - updating the searchQuery based on input value
     * - opening full SERP on pressing enter
     * - handling instant search window repositioning on resize
     */
    useEffect(() => {
        const handleInput = (event: Event) => {
            const target = event.target as HTMLInputElement;
            updateSearchQuery(target.value);
        };

        const handleKeyDown = (event: Event) => {
            const e = event as KeyboardEvent;
            const target = e.target as Element;
            if (e.key === 'Enter' && target) {
                if (isInputElement(target)) {
                    if (target.checkValidity()) {
                        target.blur();
                        openFullSERP();
                    }
                }
            }
        };

        const nativeSearchElements = document.querySelectorAll(`${config.nativeSearchId}`);

        if (!isMobile) {
            nativeSearchElements.forEach((nativeSearchElement) => {
                window.addEventListener('resize', () => {
                    positionInstantSearch(nativeSearchElement);
                });
            });
        }

        nativeSearchElements.forEach((nativeSearchElement) => {
            nativeSearchElement.addEventListener('input', handleInput);
            nativeSearchElement.addEventListener('keydown', handleKeyDown);
        });
        return () => {
            nativeSearchElements.forEach((nativeSearchElement) => {
                nativeSearchElement.removeEventListener('input', handleInput);
                nativeSearchElement.removeEventListener('keydown', handleKeyDown);
            });
        };
    }, [config.nativeSearchId]);

    // Initial native search positioning
    useEffect(() => {
        const nativeSearchElements = document.querySelectorAll(`${config.nativeSearchId}`);
        nativeSearchElements.forEach((nativeSearchElement) => {
            positionInstantSearch(nativeSearchElement);
        });
    }, [instantSearchOpen, config.nativeSearchId]);

    // Open window only when query > 2
    useEffect(() => {
        if (searchQuery.length >= 2) {
            setInstantSearchOpen(true);
        }
        if (searchQuery.length === 0) {
            setInstantSearchOpen(false);
        }
    }, [searchQuery]);

    // Open full SERP when the search trigger button is clicked.
    useEffect(() => {
        if (!instantSearchConfig?.nativeSearchElements?.trigger) {
            return;
        }
        const instantSearchTriggerElements = document.querySelectorAll(
            `${instantSearchConfig?.nativeSearchElements?.trigger}`
        );

        const handleClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (searchQuery.length >= 2) {
                openFullSERP();
            }
        };

        const clickHandler = (event: Event) => handleClick(event as MouseEvent);

        instantSearchTriggerElements.forEach((instantSearchTriggerElement) => {
            instantSearchTriggerElement.addEventListener('mousedown', clickHandler, true);
        });

        return () => {
            instantSearchTriggerElements.forEach((instantSearchTriggerElement) => {
                instantSearchTriggerElement.removeEventListener('mousedown', clickHandler, true);
            });
        };
    }, [searchQuery]);

    if (!instantSearchConfig) {
        return;
    }

    const showProductResults = instantSearchConfig.layout?.showProductResults;
    const displayType = instantSearchConfig.layout?.displayType;

    const sidebarAtStart =
        instantSearchConfig.layout?.sidebarHorizontalPosition === 'left' ||
        instantSearchConfig.layout?.sidebarVerticalPosition === 'top';

    if (!instantSearchOpen) {
        return null;
    }

    return (
        <>
            <InstantSearchBackdrop />
            <div className="serp-page" ref={innerSERPRef}>
                <div
                    ref={instantSearchRef}
                    className={classNames(
                        styles.instantSearch,
                        displayType === 'grid' ? styles.gridView : styles.rowsView
                    )}
                >
                    <div className={styles.instantSearchCaret} ref={caretRef}></div>
                    <div
                        className={classNames(
                            styles.instantSearchInner,
                            displayType === 'grid' ? styles.gridView : styles.rowsView
                        )}
                    >
                        <button className={styles.instantSearchClose} onClick={closeSERP} aria-label="Zavřít">
                            <RoundedXMarkIcon
                                width={32}
                                height={32}
                                fill="currentColor"
                                className="bi bi-x"
                                viewBox="0 0 16 16"
                            />
                        </button>
                        {sidebarAtStart && (
                            <InstantSearchSidebar
                                enableSidebar={instantSearchConfig?.layout?.showSidebar}
                                displayType={displayType}
                            />
                        )}
                        {showProductResults && (
                            <InstantProductResults
                                handleGoToResult={handleGoToResult}
                                openFullSERP={openFullSERP}
                                displayType={displayType}
                            />
                        )}
                        {!sidebarAtStart && (
                            <InstantSearchSidebar
                                enableSidebar={instantSearchConfig?.layout?.showSidebar}
                                displayType={displayType}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default InstantSearch;
